import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import Reveal from "@/components/SectionReveal";
import { GOOGLE_REVIEWS } from "@/data/googleReviews";
import { SITE } from "@/data/site";

// Inline Google "G" logo SVG
function GoogleG({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C40.3 35 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function Stars({ value = 5 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
        />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl bg-white/85 backdrop-blur border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 text-center">
      <GoogleG className="w-10 h-10 mx-auto" />
      <p className="mt-4 font-heading text-xl font-bold text-slate-900">
        Be one of our first Google reviewers
      </p>
      <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
        We just turned on Google reviews for Tampa Bay Pool Pros. If we&apos;ve cleaned your pool,
        we&apos;d love a quick star rating &amp; note.
      </p>
      <a
        href={SITE.googleReviews}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="leave-review-cta"
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white font-bold text-sm hover:bg-sky-700 transition-colors"
      >
        <GoogleG className="w-4 h-4" />
        Leave a Google review
      </a>
    </div>
  );
}

export default function GoogleReviewsSlider() {
  const reviews = GOOGLE_REVIEWS;
  const [idx, setIdx] = useState(0);

  // Auto-advance every 6s
  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [reviews.length]);

  const go = (dir) => {
    const len = reviews.length;
    if (!len) return;
    setIdx((i) => (i + dir + len) % len);
  };

  return (
    <section
      className="relative py-12 sm:py-16"
      data-testid="google-reviews-slider"
      aria-labelledby="google-reviews-heading"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
            <div>
              <p className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
                <GoogleG className="w-4 h-4" /> Google Reviews
              </p>
              <h2
                id="google-reviews-heading"
                className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900"
              >
                What Tampa Bay says about us.
              </h2>
            </div>
            <a
              href={SITE.googleReviews}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="all-reviews-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-sky-700 self-start sm:self-auto"
            >
              See all reviews on Google
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </Reveal>

        {reviews.length === 0 ? (
          <EmptyState />
        ) : (
          <Reveal>
            <div className="relative">
              <div className="relative overflow-hidden rounded-3xl bg-white/90 backdrop-blur border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
                <AnimatePresence mode="wait">
                  <motion.article
                    key={idx}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="p-6 sm:p-10"
                    data-testid={`review-slide-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      {reviews[idx].avatar ? (
                        <img
                          src={reviews[idx].avatar}
                          alt={reviews[idx].name}
                          className="w-11 h-11 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 text-white inline-flex items-center justify-center font-bold">
                          {reviews[idx].name?.[0] || "?"}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-900">{reviews[idx].name}</p>
                        <p className="text-xs text-slate-500">
                          {reviews[idx].city ? `${reviews[idx].city} · ` : ""}
                          {reviews[idx].date || ""}
                        </p>
                      </div>
                      <div className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500">
                        <GoogleG className="w-4 h-4" />
                        Google
                      </div>
                    </div>
                    <div className="mt-4">
                      <Stars value={reviews[idx].rating || 5} />
                      <blockquote className="mt-3 text-slate-800 leading-relaxed text-base sm:text-lg">
                        &ldquo;{reviews[idx].quote}&rdquo;
                      </blockquote>
                    </div>
                  </motion.article>
                </AnimatePresence>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous review"
                  data-testid="review-prev"
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 inline-flex items-center justify-center hover:border-sky-500 hover:text-sky-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1.5">
                  {reviews.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIdx(i)}
                      aria-label={`Go to review ${i + 1}`}
                      data-testid={`review-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === idx ? "w-6 bg-slate-900" : "w-1.5 bg-slate-300 hover:bg-slate-500"
                      }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next review"
                  data-testid="review-next"
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 inline-flex items-center justify-center hover:border-sky-500 hover:text-sky-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
